/**
 * Cline target importer: .clinerules (rules + workflows), .clineignore,
 * .cline/cline_mcp_settings.json, .cline/skills into canonical .agentsmesh/.
 *
 * Kept imperative on purpose. Cline's `.clinerules` may be a flat FILE or a
 * DIRECTORY, and workflows must be skipped in the file case — that runtime
 * branch is not declarable through `runDescriptorImport`. The MCP parser
 * also handles the legacy `transportType` field (project-only target — no
 * scope variance, so no descriptor-level scope-leakage to absorb).
 */

import { join } from 'node:path';
import type { ImportResult } from '../../core/types.js';
import { createImportReferenceNormalizer } from '../../core/reference/import-rewriter.js';
import { readFileSafe, writeFileAtomic, mkdirp } from '../../utils/filesystem/fs.js';
import { importFileDirectory } from '../import/import-orchestrator.js';
import { mapClineAgentFile, mapClineWorkflowFile } from './importer-mappers.js';
import { importClineRules } from './importer-rules.js';
import {
  CLINE_TARGET,
  CLINE_IGNORE,
  CLINE_WORKFLOWS_DIR,
  CLINE_CANONICAL_COMMANDS_DIR,
  CLINE_CANONICAL_IGNORE,
  CLINE_AGENTS_DIR,
  CLINE_CANONICAL_AGENTS_DIR,
} from './constants.js';
import { importClineMcp } from './mcp-mapper.js';
import { importClineSkills } from './skills-adapter.js';
import { importClineHooks } from './hook-importer.js';

/**
 * Import Cline config into canonical .agentsmesh/.
 * Sources: .clinerules (rules), .clineignore (ignore), .cline/cline_mcp_settings.json (mcp),
 * .cline/skills (skills).
 *
 * @param projectRoot - Project root directory
 * @returns Import results for each imported file
 */
export async function importFromCline(projectRoot: string): Promise<ImportResult[]> {
  const results: ImportResult[] = [];
  const normalize = await createImportReferenceNormalizer(CLINE_TARGET, projectRoot);
  const clineRulesIsFile = await importClineRules(projectRoot, results, normalize);

  const ignorePath = join(projectRoot, CLINE_IGNORE);
  const ignoreContent = await readFileSafe(ignorePath);
  if (ignoreContent !== null && ignoreContent.trim()) {
    const lines = ignoreContent.split(/\r?\n/);
    const patterns: string[] = [];
    for (const line of lines) {
      const t = line.trim();
      if (t && !t.startsWith('#')) patterns.push(t);
    }
    if (patterns.length > 0) {
      await mkdirp(join(projectRoot, '.agentsmesh'));
      const destIgnorePath = join(projectRoot, CLINE_CANONICAL_IGNORE);
      await writeFileAtomic(destIgnorePath, patterns.join('\n'));
      results.push({
        fromTool: 'cline',
        fromPath: ignorePath,
        toPath: CLINE_CANONICAL_IGNORE,
        feature: 'ignore',
      });
    }
  }

  await importClineMcp(projectRoot, results);

  const destCommandsDir = join(projectRoot, CLINE_CANONICAL_COMMANDS_DIR);
  if (!clineRulesIsFile) {
    results.push(
      ...(await importFileDirectory({
        srcDir: join(projectRoot, CLINE_WORKFLOWS_DIR),
        destDir: destCommandsDir,
        extensions: ['.md'],
        fromTool: 'cline',
        normalize,
        mapEntry: ({ relativePath, normalizeTo }) =>
          mapClineWorkflowFile(relativePath, destCommandsDir, normalizeTo),
      })),
    );
  }

  await importClineSkills(projectRoot, results, normalize);
  results.push(
    ...(await importFileDirectory({
      srcDir: join(projectRoot, CLINE_AGENTS_DIR),
      destDir: join(projectRoot, CLINE_CANONICAL_AGENTS_DIR),
      extensions: ['.md'],
      fromTool: 'cline',
      normalize,
      mapEntry: ({ relativePath, normalizeTo }) =>
        mapClineAgentFile(relativePath, join(projectRoot, CLINE_CANONICAL_AGENTS_DIR), normalizeTo),
    })),
  );
  await importClineHooks(projectRoot, results);

  return results;
}
